import type { Optional } from '@festina-life/core'
import {
  type FileContents,
  type FileDiffMetadata,
  parseDiffFromFile,
} from '@pierre/diffs'

import { stringifyComparableValue } from '../diffIndicators/helpers'
import { fplusFileExtension } from './fplusDiffHighlighting'

/**
 * The name a diff side carries. It is never shown to a reader — it exists so
 * the renderer infers F-plus from its extension.
 */
export const fplusDiffFileName = `expression.${fplusFileExtension}`

/**
 * The name the sides of a prose comparison carry. Its extension is one the
 * renderer knows nothing about, which is exactly the point: it falls back to
 * plain text, so documentation is compared as the prose it is rather than
 * highlighted as some language it was mistaken for.
 */
export const textDiffFileName = 'text.txt'

export type DiffSides = {
  /** The expression being compared against, on the left of the diff. */
  compare: Optional<string>
  /** The expression as it stands now, on the right of the diff. */
  current: Optional<string>
}

export type Diff =
  /** The two sides read the same, so there is nothing worth showing. */
  { isChanged: false } | { isChanged: true; fileDiff: FileDiffMetadata }

/**
 * What became of a value between the two sides of a comparison: it was written
 * where there was none, taken away, changed, or left alone.
 */
export type ValueComparison = 'added' | 'removed' | 'modified' | 'unchanged'

/**
 * An expression is not a file, so the renderer's "no newline at end of file"
 * note tells a reader nothing they could act on. Terminating both sides the
 * same way removes it without changing what is compared.
 */
const asDiffContents = (source: string): string =>
  source.endsWith('\n') ? source : `${source}\n`

/**
 * A side with no expression on it is still a side: it is shown as itself, empty,
 * opposite the one that has something to read. Handing the renderer nothing at
 * all would instead collapse the comparison into a single column, which reads
 * the same whichever side went missing and leaves a reader who asked to see two
 * of them looking at one.
 *
 * An expression emptied out and one that was never there are shown alike,
 * because they are the same thing to read: this side says nothing.
 */
const asDiffSide = (source: Optional<string>, name: string): FileContents => ({
  name,
  contents: source ? asDiffContents(source) : '',
})

/**
 * Both sides of one comparison carry the same name, because the name only
 * exists to name the language. The renderer caches highlighted lines per diff
 * and falls back to the name when no key is given, so the two expressions
 * themselves are the key: without it every diff in the portal would share one
 * cache entry and be shown the first expression that was highlighted.
 *
 * The key is derived from both sides together rather than left to the renderer
 * to combine, because a one-sided comparison gives it only one side to work
 * from, which would key an expression added identically to the same expression
 * removed. The two sides are kept apart unambiguously rather than joined by a
 * separator, because any separator can also occur inside an expression, and two
 * comparisons keyed alike are shown each other's highlighting.
 *
 * The name is part of the key too, because the same text compared as F-plus and
 * as prose is highlighted differently, and keying the two alike would show one
 * of them the other's highlighting.
 */
const cacheKeyOf = (oldFile: FileContents, newFile: FileContents): string =>
  JSON.stringify([oldFile.name, oldFile.contents, newFile.contents])

/**
 * Decides whether two sides differ, and if they do, describes the diff between
 * them. Both sides are expected to have been laid out alike already; this
 * decides only what differs between them.
 *
 * The name both sides carry decides how the renderer reads them — as F-plus, or
 * as plain prose.
 */
const buildDiff = ({ compare, current }: DiffSides, fileName: string): Diff => {
  const oldFile = asDiffSide(compare, fileName)
  const newFile = asDiffSide(current, fileName)

  if (oldFile.contents === newFile.contents) {
    return { isChanged: false }
  }

  return {
    isChanged: true,
    fileDiff: {
      ...parseDiffFromFile(oldFile, newFile),
      // We don't support showing adds and deletes. All diffs are shown as changes since
      // we always want to show the change side-by-side.
      type: 'change',
      cacheKey: cacheKeyOf(oldFile, newFile),
    },
  }
}

/**
 * The diff between two F-plus expressions, highlighted as F-plus.
 */
export const buildFPlusDiff = (sides: DiffSides): Diff =>
  buildDiff(sides, fplusDiffFileName)

/**
 * The diff between two pieces of prose, such as a field's documentation, read
 * as the plain text it is.
 */
export const buildTextDiff = (sides: DiffSides): Diff =>
  buildDiff(sides, textDiffFileName)

/**
 * What a comparison amounts to, for the rows that are presented as a before
 * beside an after rather than as a diff — identifiers, parent references,
 * booleans, enumerations and type config, and the entries of a list.
 *
 * It is the same question `buildFPlusDiff` and `buildTextDiff` answer for the
 * rows they serve, asked of values that are not text: a comparison of two values
 * that read the same is nothing a reader came to see, so the row is left out
 * altogether and the page shows only what actually differs. Every kind of value
 * is compared through the one stringifier, so a row's fate never depends on
 * which component happens to draw it.
 *
 * A value that was emptied out and one that was never there read the same, and
 * so are the same to this rule — as they are to the diffs.
 *
 * The suppression of an inherited value that has nothing of its own is a
 * separate concern and stays with the components that know about inheritance: it
 * asks whether a comparison is worth making at all, not whether two values
 * differ.
 */
export const compareValues = (
  compare: unknown,
  current: unknown,
): ValueComparison => {
  const comparedText = stringifyComparableValue(compare)
  const currentText = stringifyComparableValue(current)

  if (comparedText === currentText) {
    return 'unchanged'
  }

  if (!comparedText) {
    return 'added'
  }

  return currentText ? 'modified' : 'removed'
}

/**
 * Whether a comparison has nothing to show, and so no row to draw.
 */
export const isComparisonUnchanged = (
  compare: unknown,
  current: unknown,
): boolean => compareValues(compare, current) === 'unchanged'
